import { type FC, useEffect, useMemo, useRef, useState } from "react";

import type {
  IssueDetail,
  IssueListItem,
  IssueListPage,
  IssueProviderSummary,
  NormalizedIssueStatus,
} from "./types";

import { RetroEmpty, RetroError, SectionLabel } from "../retro";
import {
  getIssueDetail,
  listIssueProviders,
  listIssues,
  listProviderProjects,
} from "./api";

// Mirrors the server's exact-key detection (issues.ts isExactIssueKey).
const ISSUE_KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_]*-\d+$/u;

type WorkState = "active" | "all";

const STATUS_CHIP_CLASSES: Record<NormalizedIssueStatus, string> = {
  Done: "border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400",
  "In Progress": "border-amber-500/40 text-amber-700 dark:text-amber-300",
  "To Do": "border-sky-500/40 text-sky-700 dark:text-sky-300",
};

function formatRelativeTime(epochSeconds: number): string {
  const deltaSeconds = Math.max(
    0,
    Math.floor(Date.now() / 1000) - epochSeconds,
  );
  if (deltaSeconds < 3600) {
    return `${Math.max(1, Math.floor(deltaSeconds / 60))}m`;
  }
  if (deltaSeconds < 86_400) {
    return `${Math.floor(deltaSeconds / 3600)}h`;
  }
  if (deltaSeconds < 604_800) {
    return `${Math.floor(deltaSeconds / 86_400)}d`;
  }
  return `${Math.floor(deltaSeconds / 604_800)}w`;
}

interface TicketStepProps {
  selectedIssue: IssueDetail | null;
  onSelectIssue: (issue: IssueDetail | null) => void;
}

/** Step 1 — pick the Jira ticket. Provider auto-selected when there is only
 * one; a configured default project immediately loads recent issues so the
 * first render is a ticket list, not a blank search box. Pasting an exact
 * issue key skips search. */
export const TicketStep: FC<TicketStepProps> = ({
  selectedIssue,
  onSelectIssue,
}) => {
  const [providers, setProviders] = useState<IssueProviderSummary[] | null>(
    null,
  );
  const [providerId, setProviderId] = useState<string | null>(null);
  const [projects, setProjects] = useState<{ key: string; name: string }[]>([]);
  const [projectKey, setProjectKey] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [workState, setWorkState] = useState<WorkState>("active");
  const [issues, setIssues] = useState<IssueListItem[]>([]);
  const [page, setPage] = useState<IssueListPage>({
    hasMore: false,
    nextCursor: null,
  });
  const [searching, setSearching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState<string | null>(null);
  const [errorMessage, setError] = useState<string | null>(null);
  // Bumped by every fresh search so a stale load-more can't append onto it.
  const searchSeqRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    void listIssueProviders()
      .then((loaded) => {
        if (cancelled) {
          return;
        }
        setProviders(loaded);
        const enabled = loaded.filter((provider) => provider.enabled);
        if (enabled.length === 1) {
          setProviderId(enabled[0].id);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setError(error instanceof Error ? error.message : String(error));
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const provider = useMemo(
    () => providers?.find((candidate) => candidate.id === providerId) ?? null,
    [providers, providerId],
  );

  // Load the provider's configured projects, then apply selection precedence:
  // valid default project → only configured project → explicit choice.
  useEffect(() => {
    if (!providerId) {
      return;
    }
    let cancelled = false;
    setProjects([]);
    setProjectKey(null);
    void listProviderProjects(providerId)
      .then((loaded) => {
        if (cancelled) {
          return;
        }
        setProjects(loaded);
        const defaultKey = provider?.defaultProject ?? null;
        if (defaultKey && loaded.some((p) => p.key === defaultKey)) {
          setProjectKey(defaultKey);
        } else if (loaded.length === 1) {
          setProjectKey(loaded[0].key);
        }
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setError(error instanceof Error ? error.message : String(error));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [providerId, provider]);

  // Recent issues for the selected project, or search results while typing.
  // Summary search requires a project; only an exact key may look up without
  // one — never silently search the provider's entire estate.
  useEffect(() => {
    searchSeqRef.current += 1;
    const trimmed = query.trim();
    const isExactKey = ISSUE_KEY_PATTERN.test(trimmed);
    if (!providerId || (!projectKey && !isExactKey)) {
      setIssues([]);
      setPage({ hasMore: false, nextCursor: null });
      // A cancelled in-flight search skips its own reset in finally, so clear
      // the indicator here or "Searching…" sticks forever.
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    setError(null);
    const timer = setTimeout(() => {
      void listIssues({
        providerId,
        workState,
        ...(projectKey ? { projectKey } : {}),
        ...(trimmed ? { q: trimmed } : {}),
      })
        .then((loaded) => {
          if (!cancelled) {
            setIssues(loaded.issues);
            setPage(loaded.page);
          }
        })
        .catch((error: unknown) => {
          if (!cancelled) {
            setError(error instanceof Error ? error.message : String(error));
          }
        })
        .finally(() => {
          if (!cancelled) {
            setSearching(false);
          }
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [providerId, projectKey, query, workState]);

  const loadMore = () => {
    if (!providerId || !page.nextCursor || searching || loadingMore) {
      return;
    }
    const seq = searchSeqRef.current;
    const trimmed = query.trim();
    const cursor = page.nextCursor;
    setLoadingMore(true);
    void listIssues({
      cursor,
      providerId,
      workState,
      ...(projectKey ? { projectKey } : {}),
      ...(trimmed ? { q: trimmed } : {}),
    })
      .then((loaded) => {
        if (searchSeqRef.current !== seq) {
          return;
        }
        setIssues((previous) => {
          const seen = new Set(
            previous.map((issue) => `${issue.providerId}:${issue.issueKey}`),
          );
          return [
            ...previous,
            ...loaded.issues.filter(
              (issue) => !seen.has(`${issue.providerId}:${issue.issueKey}`),
            ),
          ];
        });
        setPage(loaded.page);
      })
      .catch((error: unknown) => {
        if (searchSeqRef.current === seq) {
          setError(error instanceof Error ? error.message : String(error));
        }
      })
      .finally(() => setLoadingMore(false));
  };

  const selectIssue = (issue: IssueListItem) => {
    setLoadingDetail(issue.issueKey);
    setError(null);
    void getIssueDetail(issue.providerId, issue.issueKey)
      .then((detail) => onSelectIssue(detail))
      .catch((error: unknown) => {
        setError(error instanceof Error ? error.message : String(error));
      })
      .finally(() => setLoadingDetail(null));
  };

  if (providers !== null && providers.filter((p) => p.enabled).length === 0) {
    return (
      <RetroEmpty
        label="No issue integration configured"
        sub="Connect Jira under Settings → Integrations, then come back here."
      />
    );
  }

  return (
    <div className="space-y-4">
      {providers !== null && providers.filter((p) => p.enabled).length > 1 && (
        <div>
          <SectionLabel label="Provider" />
          <div className="mt-1 flex flex-wrap gap-1.5">
            {providers
              .filter((candidate) => candidate.enabled)
              .map((candidate) => (
                <button
                  key={candidate.id}
                  type="button"
                  onClick={() => setProviderId(candidate.id)}
                  className={`border px-2 py-1 font-mono text-[11px] transition-colors ${
                    candidate.id === providerId
                      ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                      : "border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-400 dark:hover:border-slate-500"
                  }`}
                >
                  {candidate.displayName}
                </button>
              ))}
          </div>
        </div>
      )}

      {providerId && (
        <div className="flex flex-wrap items-end gap-3">
          {projects.length > 1 && (
            <div>
              <SectionLabel label="Project" />
              <select
                value={projectKey ?? ""}
                onChange={(event) => setProjectKey(event.target.value || null)}
                className="mt-1 border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 font-mono text-xs text-slate-700 dark:text-slate-200"
              >
                <option value="">Choose a project…</option>
                {projects.map((project) => (
                  <option key={project.key} value={project.key}>
                    {project.key} · {project.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="min-w-0 flex-1">
            <SectionLabel label="Ticket" />
            <input
              type="text"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Paste a key (PROJ-123) or search summaries…"
              className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-2 py-1.5 font-mono text-xs text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-600"
            />
          </div>
          <div>
            <SectionLabel label="Show" />
            <div className="mt-1 flex gap-1.5">
              {(
                [
                  { label: "Open", value: "active" },
                  { label: "All", value: "all" },
                ] as const
              ).map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setWorkState(option.value)}
                  className={`border px-2 py-1 font-mono text-[11px] transition-colors ${
                    workState === option.value
                      ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                      : "border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-400 dark:hover:border-slate-500"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {errorMessage && <RetroError message={errorMessage} />}

      {selectedIssue && (
        <div className="border border-emerald-500/40 bg-emerald-500/5 p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-mono text-[11px] font-bold text-emerald-700 dark:text-emerald-300">
                {selectedIssue.issueKey}
              </div>
              <div className="truncate text-sm text-slate-700 dark:text-slate-200">
                {selectedIssue.summary}
              </div>
              <div className="mt-1 font-mono text-[10px] text-slate-500 dark:text-slate-400">
                {[
                  selectedIssue.status,
                  selectedIssue.issueType,
                  selectedIssue.assignee,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onSelectIssue(null)}
              className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300"
            >
              Change
            </button>
          </div>
        </div>
      )}

      {!selectedIssue && providerId && (
        <div className="max-h-72 space-y-1 overflow-y-auto">
          {searching && (
            <div className="py-4 text-center font-mono text-[11px] text-slate-500 dark:text-slate-400">
              {ISSUE_KEY_PATTERN.test(query.trim())
                ? `Looking up ${query.trim().toUpperCase()}…`
                : "Searching…"}
            </div>
          )}
          {!searching &&
            !projectKey &&
            query.trim() !== "" &&
            !ISSUE_KEY_PATTERN.test(query.trim()) && (
              <div className="py-4 text-center font-mono text-[11px] text-slate-500 dark:text-slate-400">
                Select a project to search summaries, or paste an exact key.
              </div>
            )}
          {!searching &&
            issues.length === 0 &&
            (projectKey || ISSUE_KEY_PATTERN.test(query.trim())) && (
              <div className="space-y-2 py-4 text-center font-mono text-[11px] text-slate-500 dark:text-slate-400">
                <div>
                  {projectKey
                    ? `No ${workState === "active" ? "open " : ""}issues in ${projectKey}${query.trim() ? " match" : ""}`
                    : "No matches"}
                </div>
                {workState === "active" && (
                  <button
                    type="button"
                    onClick={() => setWorkState("all")}
                    className="border border-slate-300 dark:border-slate-700 px-2 py-1 text-slate-500 dark:text-slate-400 transition-colors hover:border-slate-400 dark:hover:border-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                  >
                    Search completed tickets too
                  </button>
                )}
              </div>
            )}
          {!searching &&
            issues.map((issue) => (
              <button
                key={`${issue.providerId}:${issue.issueKey}`}
                type="button"
                onClick={() => selectIssue(issue)}
                disabled={loadingDetail !== null}
                className="w-full border border-slate-200 dark:border-slate-800 px-3 py-2 text-left transition-colors hover:border-slate-300 dark:hover:border-slate-600 disabled:opacity-50"
              >
                <div className="flex items-center gap-3">
                  <span className="shrink-0 font-mono text-[11px] font-bold text-emerald-600 dark:text-emerald-400">
                    {loadingDetail === issue.issueKey ? "…" : issue.issueKey}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-xs text-slate-600 dark:text-slate-300">
                    {issue.summary}
                  </span>
                  {issue.status && (
                    <span
                      className={`shrink-0 border px-1.5 py-0.5 font-mono text-[10px] ${
                        issue.normalizedStatus
                          ? STATUS_CHIP_CLASSES[issue.normalizedStatus]
                          : "border-slate-300 dark:border-slate-700 text-slate-500 dark:text-slate-400"
                      }`}
                    >
                      {issue.status}
                    </span>
                  )}
                </div>
                <div className="mt-1 truncate font-mono text-[10px] text-slate-500 dark:text-slate-400">
                  {[
                    issue.issueType,
                    issue.assignee ?? "Unassigned",
                    issue.updatedAt
                      ? formatRelativeTime(issue.updatedAt)
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </button>
            ))}
          {!searching && issues.length > 0 && page.hasMore && (
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              className="w-full border border-dashed border-slate-300 dark:border-slate-700 px-3 py-2 text-center font-mono text-[11px] text-slate-500 dark:text-slate-400 transition-colors hover:border-slate-400 dark:hover:border-slate-500 hover:text-slate-700 dark:hover:text-slate-300 disabled:opacity-50"
            >
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
